import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core'
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  NgForm,
  Validators
} from '@angular/forms'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { Store } from '@ngrx/store'
import { Observable, Subscription, combineLatest } from 'rxjs'
import {
  distinctUntilChanged,
  map,
  pairwise,
  skip,
  startWith,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { getEnumAsArray, hexColorToDecimal } from 'src/app/shared/until'
import { Location } from '../../location/location'
import { locationActions } from '../../location/location.actions'
import { locationSelectors } from '../../location/location.selectors'
import { Product } from '../../product/product'
import { productActions } from '../../product/product.actions'
import { productSelectors } from '../../product/product.selectors'
import { Booking, BookingCreationType, BookingStatus } from '../booking'
import moment from 'moment-mini'
import { NotificationService } from 'src/app/core/notification.service'
import { Dictionary } from '@ngrx/entity'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { ActivatedRoute } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { persistentUserRoleSelectors } from '../../role/user-role/persistent-user-role/persistent-user-role.selector'
import { DEFAULT_COLOR, SupervisorAssignedType } from 'src/app/shared/constants'

enum EndTimeSlot {
  PRODUCT_SLOT_SIZE = 'slotSize',
  ONE_HOUR = '1hor',
  ONE_AND_HALF_HOURS = '1.5hour',
  TWO_HOURS = '2hour',
  CUSTOM = 'custom'
}
@UntilDestroy()
@Component({
  selector: 'booking-multi-create-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class BookingMultiCreateFormComponent implements OnInit, OnDestroy {
  @Input() wantApplyYearMatrix: boolean = true
  @Input() showApplyYearCalendar: boolean = false
  @Input() resetRepeatForm$: Observable<boolean>
  @Input() booking: Partial<Booking>
  @Input() repeatBooking: boolean = false
  @Input() formOfRepeatFormRepeat: UntypedFormGroup
  @Input() bookingOfRepeatFormRepeat: Partial<Booking>
  @Input() assignSupervisor: boolean = false
  @Input() disableAssignSupervisorButtons: boolean = true
  @Input() disableRepeatBookingButtons: boolean = true
  @Input() selectedBookingCreateType: BookingCreationType =
    BookingCreationType.DEFAULT
  @Input() selectedDate$: Observable<any>
  @Input() linkedProductChange$: Observable<Product>
  @Input() createBookingFromSchedule: boolean = false
  @Input() showRepeatForm: boolean = false
  @Input() courseProduct: Product | null = null

  @Input() form: UntypedFormGroup
  @Input() arraySupervisors: number[] | SupervisorAssignedType[] = []
  @Input() resetSupervisorBookingsData$: Observable<boolean>
  @Output() onSubmit = new EventEmitter<any>()
  @Output() onValueChanges = new EventEmitter<any>()
  @Output() onApplyYearCalendar = new EventEmitter<any>()
  @Output() onOpenModalRepeatbookings = new EventEmitter<boolean>()
  @Output() onClickCancelButtonRepeat = new EventEmitter<any>()
  @Output() onClickOnSaveButtonRepeat = new EventEmitter<any>()
  @Output() onSetSelectedFormInBooKingFormRepeat = new EventEmitter<any>()
  @Output() onClickOpenModalAssignSupervisor = new EventEmitter<any>()
  @Output() onDateAdjustment = new EventEmitter<boolean>()
  public showHideFields: boolean = false
  public endTimeSlot = EndTimeSlot
  public selectedEndTimeSlot: EndTimeSlot = EndTimeSlot.ONE_HOUR
  public allProducts$: Observable<Product[]>
  public allActiveProducts$: Observable<Product[]>
  public productEntities$: Observable<Dictionary<Product>>
  public bookableProducts$: Observable<Product[]>
  public allowAsLinkedChildProducts$: Observable<Product[]>
  public default_booking_color = DEFAULT_COLOR
  public changeOfResourceProduct: boolean = false
  public location$: Observable<Location>
  private lastStartDate: any
  private lastLinkedProductIdForDefaultTimes: number = null
  public bookingStatus = getEnumAsArray(BookingStatus)
  public BookingCreationType = BookingCreationType
  public mappedProducts$: Observable<Product[]>
  public selectedLinkedProductId$: Observable<number>
  public allowedLinkedProductIds$: Observable<number[]>
  public selectedProductIds: number[] = []
  @ViewChild('formRef') formRef: NgForm
  private subscriptions: Subscription[] = []
  private INITIAL_LIMIT = 25
  constructor(
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private notificationService: NotificationService
  ) {}
  ngOnInit() {
    this.initForm()
    this.getProductsSelectors()
    this.dispatchFetches()
    this.linkedProductChangeSubscription()
    this.formValueChangesSubsAndSelectedDateSubs()
    this.selectedLinkedAndAllowedSubscriptions()
    this.handleSelectionBookingCreationType()
    this.mappedProductsSubscription()
    this.allowAsLinkedChildProductsSubscription()
    this.locationSubscription()
    this.setDefaultColor()
    this.handleDateChanges()
  }
  private initForm() {
    this.form = this.fb.group({
      linkedProductId: [null, []],
      productIds: [null, [Validators.required]],
      description: [null, []],
      maxParticipants: [null, [Validators.min(0)]],
      courseId: [null, []],
      startDate: [
        null,
        [Validators.required, Validators.minLength(5), this.dateValidator]
      ],
      colour: [null, []],
      endDate: [null, [Validators.required, this.dateValidator]],
      status: [0, [Validators.required]],
      memo: [null, []],
      serieId: [null, []],
      allowedLinkedProductIds: [null]
    })
  }

  private dispatchFetches() {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            isActive: true,
            $or: [
              {
                ['isCourse']: true
              },
              {
                ['allowAsLinkedProduct']: true
              },
              {
                ['bookable']: true
              }
            ]
          },
          limit: this.INITIAL_LIMIT,
          join: [
            { field: 'parentBookableProducts' },
            { field: 'dependantProducts' }
          ]
        })
      })
    )

    this.store.dispatch(locationActions.fetchMany({}))
  }

  private getProductsSelectors() {
    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.allProducts$ = this.store.select(productSelectors.selectAll).pipe(
      withLatestFrom(
        this.store.select(persistentUserRoleSelectors.selectSelectedUserRole)
      ),
      map(([products, persistentUserRole]) =>
        products.filter(
          (product) =>
            product.siteId == persistentUserRole?.siteId ||
            product.siteId == null ||
            product.siteId == 0
        )
      )
    )
    this.allActiveProducts$ = this.store
      .select(productSelectors.selectAll)
      .pipe(map((product) => product.filter((p) => p.isActive)))
  }

  private linkedProductChangeSubscription() {
    this.linkedProductChange$
      .pipe(untilDestroyed(this))
      .subscribe((selectedLinkedProduct) => {
        if (!selectedLinkedProduct) {
          this.lastLinkedProductIdForDefaultTimes = null
          return
        }
        if (
          this.lastLinkedProductIdForDefaultTimes === selectedLinkedProduct.id
        ) {
          return
        }
        this.lastLinkedProductIdForDefaultTimes = selectedLinkedProduct.id
        const defaultStartTime = selectedLinkedProduct.defaultStartTime
        const defaultEndTime = selectedLinkedProduct.defaultEndTime
        const slotMinutes = selectedLinkedProduct.slotMinutes
        const setTime = (
          timeValue: string | moment.Moment | null | undefined
        ): moment.Moment | null => {
          if (!timeValue) return null

          const momentObject = moment(timeValue)
          if (momentObject.isValid()) {
            return momentObject
          }
          if (typeof timeValue === 'string') {
            const [hour, minute] = timeValue.split(':').map(Number)
            if (!isNaN(hour) && !isNaN(minute)) {
              return moment().set({ hour, minute, second: 0, millisecond: 0 })
            }
          }
          return null
        }
        let startDate = setTime(defaultStartTime)
        let endDate = setTime(defaultEndTime)
        const isStartValid = !!startDate
        const isEndValid = !!endDate
        if (isStartValid && isEndValid) {
          this.form.controls.startDate.patchValue(startDate)
          this.form.controls.endDate.patchValue(endDate)
        } else if (isStartValid && !isEndValid) {
          this.form.controls.startDate.patchValue(startDate)
          this.form.controls.endDate.patchValue(
            startDate.clone().add(slotMinutes, 'minutes')
          )
        } else if (!isStartValid && isEndValid) {
          this.form.controls.startDate.patchValue(
            endDate.clone().subtract(slotMinutes, 'minutes')
          )
          this.form.controls.endDate.patchValue(endDate)
        }
      })
  }
  private selectedDateSubscription() {
    this.selectedDate$.pipe(untilDestroyed(this)).subscribe((selectDate) => {
      if (!selectDate) return
      const form = this.form.controls
      const currentStart = moment(this.form.value.startDate)
      const currentEnd = moment(this.form.value.endDate)
      const selectedTime =
        selectDate.time && selectDate.isFromCreate ? selectDate.time : moment()

      const updateDateToSelected = (target: moment.Moment) =>
        target.clone().set({
          year: selectedTime.year(),
          month: selectedTime.month(),
          date: selectedTime.date()
        })
      if (!currentStart.isValid()) {
        const newStart = selectedTime.clone()
        const newEnd = newStart.clone().add(60, 'minutes')
        form.startDate.patchValue(newStart)
        this.lastStartDate = newStart
        form.endDate.patchValue(newEnd)
        return
      }
      const newStartDate = updateDateToSelected(
        selectDate.time && selectDate.isFromCreate
          ? selectDate.time
          : currentStart
      )

      const newEndDate = updateDateToSelected(
        currentEnd.isValid() ? currentEnd : newStartDate
      )

      form.startDate.patchValue(newStartDate)
      this.lastStartDate = newStartDate

      form.endDate.patchValue(
        selectDate.time && selectDate.isFromCreate
          ? newStartDate.clone().add(60, 'minutes')
          : newEndDate
      )
    })
  }
  private formValueChangesSubscription() {
    this.form.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => {
          this.selectedProductIds = b.productIds ? b?.productIds : []
          return JSON.stringify(a) == JSON.stringify(b)
        }),
        withLatestFrom(this.productEntities$),
        untilDestroyed(this)
      )
      .subscribe(([_, productEntities]) => {
        const f = this.form.value
        const isDefault = [BookingCreationType.DEFAULT].includes(
          this.selectedBookingCreateType
        )
        const isMultiActivityType = [
          BookingCreationType.MULTI_ACTIVITY
        ].includes(this.selectedBookingCreateType)
        if (
          f.linkedProductId &&
          !f.productIds?.length &&
          productEntities[f.linkedProductId]?.parentBookableProducts?.length
        ) {
          if (isDefault) {
            if (this.changeOfResourceProduct) {
              this.form.controls.productIds.patchValue(this.selectedProductIds)
            }
          } else {
            this.form.controls.productIds.patchValue(this.selectedProductIds)
          }
        }

        if (f.allowedLinkedProductIds && !f.productIds?.length) {
          const parentBookableProducts = f.allowedLinkedProductIds.reduce(
            (res, pId) => [
              ...res,
              ...this.getBookableProducts(pId, productEntities)
            ],
            []
          )
          if (
            parentBookableProducts?.length &&
            !isDefault &&
            !isMultiActivityType
          ) {
            this.form.controls.productIds.patchValue(
              parentBookableProducts?.map((p) => p.id)
            )
          }
        }
        if (
          ((isDefault && this.changeOfResourceProduct) ||
            isMultiActivityType) &&
          this.selectedProductIds
        ) {
          this.bookableProducts$ = this.allProducts$?.pipe(
            take(1),
            map((products) =>
              products.filter(
                (product) =>
                  (product.bookable && product.isActive) ||
                  this.selectedProductIds.includes(product.id)
              )
            )
          )
          this.form.controls.productIds.setValue(this.selectedProductIds)
        }

        if (this.lastStartDate) {
          this.setEndDate(this.selectedEndTimeSlot)
        }

        this.onValueChanges.emit({
          ...this.form.value,
          colour:
            this.form.value.colour == DEFAULT_COLOR
              ? null
              : hexColorToDecimal(this.form.value.colour)
        })
      })
  }
  private formValueChangesSubsAndSelectedDateSubs() {
    this.formValueChangesSubscription()
    this.selectedDateSubscription()
  }

  private selectedLinkedAndAllowedSubscriptions() {
    this.selectedLinkedProductId$ =
      this.form.controls.linkedProductId.valueChanges
    this.allowedLinkedProductIds$ =
      this.form.controls.allowedLinkedProductIds.valueChanges

    this.selectedLinkedProductId$
      .pipe(untilDestroyed(this))
      .subscribe((prId) => this.changeProducts(prId))

    this.allowedLinkedProductIds$
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        untilDestroyed(this)
      )
      .subscribe((prIds) => {
        this.onSelectAllowedLinkedProductIds(prIds)
      })
  }

  private handleSelectionBookingCreationType() {
    const isDefault = [BookingCreationType.DEFAULT].includes(
      this.selectedBookingCreateType
    )
    const isMultiActivityType = [BookingCreationType.MULTI_ACTIVITY].includes(
      this.selectedBookingCreateType
    )
    if (isDefault) {
      this.bookableProducts$ = combineLatest([
        this.store.select(productSelectors.selectAll),
        this.selectedLinkedProductId$.pipe(startWith(null)),
        this.productEntities$
      ]).pipe(
        pairwise(),
        untilDestroyed(this),
        map(
          ([
            [prevProducts, prevId, prevEntities],
            [products, currentId, productEntities]
          ]) => {
            const changeOfLinkedProduct = prevId !== currentId
            if (this.createBookingFromSchedule && this.booking?.product) {
              return [this.booking?.product]
            }
            if (this.changeOfResourceProduct && !changeOfLinkedProduct) {
              return products.filter(
                (product) => product.bookable && product.isActive
              )
            } else {
              this.changeOfResourceProduct = false
            }

            if (!currentId) {
              if (this.createBookingFromSchedule) {
                this.form.controls.productIds.patchValue([
                  this.booking.productId
                ])
              } else {
                return []
              }
            }

            if (
              productEntities[currentId] &&
              productEntities[currentId]?.parentBookableProducts?.length
            ) {
              const arrayActives = []
              productEntities[currentId]?.parentBookableProducts?.map(
                (parentBookable, i) => {
                  if (productEntities[parentBookable].isActive) {
                    arrayActives.push(
                      productEntities[currentId]?.parentBookableProducts[i]
                    )
                  }
                }
              )

              if (productEntities[currentId]?.isActive) {
                this.form.controls.productIds.setValue(arrayActives ?? [])
              }
            }

            return products.filter(
              (product) => product.bookable && product.isActive
            )
          }
        )
      )

      if (this.createBookingFromSchedule) {
        this.form.controls.productIds.patchValue([this.booking.productId])
      }
    } else if (isMultiActivityType) {
      const getUniqueParentBookableIds = (
        allowedLinkedProductIds: number[],
        productEntities: Dictionary<Product>
      ) => {
        if (!allowedLinkedProductIds?.length) return []

        const uniqueIds = new Set<number>()
        allowedLinkedProductIds.forEach((allowId: number) => {
          const parents = productEntities[allowId]?.parentBookableProducts ?? []
          parents.forEach((parentId: number) => uniqueIds.add(parentId))
        })

        return Array.from(uniqueIds)
      }
      this.bookableProducts$ = combineLatest([
        this.store.select(productSelectors.selectAll),
        this.allowedLinkedProductIds$
      ]).pipe(
        withLatestFrom(this.productEntities$),
        untilDestroyed(this),
        map(([[products, allowedLinkedProductIds], productEntities]) => {
          if (!allowedLinkedProductIds) {
            return []
          }

          const uniqueParentIds = [
            ...new Set(
              getUniqueParentBookableIds(
                allowedLinkedProductIds,
                productEntities
              )
            )
          ]
          this.form.controls.productIds.setValue(uniqueParentIds ?? [])
          if (uniqueParentIds?.length > 0) {
            return products.filter(
              (product) =>
                (product.bookable && product.isActive) ||
                uniqueParentIds.includes(product.id)
            )
          }

          return products.filter(
            (product) => product.bookable && product.isActive
          )
        })
      )
    } else {
      this.bookableProducts$ = this.allProducts$?.pipe(
        map((products) =>
          products.filter((product) => product.bookable && product.isActive)
        )
      )
    }
  }

  private mappedProductsSubscription() {
    const isDefault = [BookingCreationType.DEFAULT].includes(
      this.selectedBookingCreateType
    )
    const isMultiActivityType = [BookingCreationType.MULTI_ACTIVITY].includes(
      this.selectedBookingCreateType
    )
    this.mappedProducts$ = combineLatest([
      this.store.select(productSelectors.selectEntities),
      this.bookableProducts$,
      this.selectedLinkedProductId$,
      this.allowedLinkedProductIds$
    ]).pipe(
      untilDestroyed(this),
      map(
        ([
          productEntities,
          allBookableProducts,
          selectedLinkedProductId,
          allowedLinkedProductIds
        ]) => {
          if (
            isDefault &&
            selectedLinkedProductId &&
            productEntities[selectedLinkedProductId]
          ) {
            const parentIds =
              productEntities[selectedLinkedProductId]
                ?.parentBookableProducts ?? []
            return parentIds.map((id) => productEntities[id]).filter(Boolean)
          }

          if (isMultiActivityType && allowedLinkedProductIds?.length) {
            const parentIds = [
              ...new Set(
                allowedLinkedProductIds.flatMap(
                  (id) => productEntities[id]?.parentBookableProducts ?? []
                )
              )
            ]
            return parentIds.map((id) => productEntities[id]).filter(Boolean)
          }

          return allBookableProducts
        }
      ),
      tap((mappedProducts) => {
        if (this.selectedProductIds?.length) {
          this.form.controls.productIds.setValue(this.selectedProductIds)
        }
      })
    )
  }

  private allowAsLinkedChildProductsSubscription() {
    this.allowAsLinkedChildProducts$ = this.allActiveProducts$.pipe(
      map((product) => {
        return product.filter(
          (p) =>
            (Boolean(p.allowAsLinkedProduct) || Boolean(p.isCourse)) &&
            p.isActive
        )
      })
    )
  }

  private locationSubscription() {
    this.location$ = this.form.controls.productIds.valueChanges.pipe(
      withLatestFrom(
        this.store.select(productSelectors.selectEntities),
        this.store.select(locationSelectors.selectEntities)
      ),
      map(([productIds, productEntities, locationEntities]) => {
        if (!productIds) return null
        if (productIds?.length == 1) {
          return locationEntities[productEntities[productIds[0]]?.locationId]
        }
        return null
      })
    )
  }

  private setDefaultColor() {
    this.form.controls.colour.patchValue(this.default_booking_color)
  }
  private handleDateChanges() {
    combineLatest([
      this.form.controls.endDate.valueChanges.pipe(
        startWith(this.form.controls.endDate.value),
        distinctUntilChanged((prev, curr) => {
          if (!prev && !curr) return true
          if (!prev || !curr) return false
          return moment(prev).isSame(moment(curr), 'minute')
        })
      ),
      this.form.controls.startDate.valueChanges.pipe(
        startWith(this.form.controls.startDate.value),
        distinctUntilChanged((prev, curr) => {
          if (!prev && !curr) return true
          if (!prev || !curr) return false
          return moment(prev).isSame(moment(curr), 'minute')
        })
      )
    ])
      .pipe(
        skip(1),
        tap(() => this.onDateAdjustment.emit(true)),
        untilDestroyed(this)
      )
      .subscribe()
  }

  private changeProducts(productId: number) {
    if (productId) {
      this.store.dispatch(
        productActions.fetchOneById({
          id: productId,
          params: RequestQueryBuilder.create({
            join: [
              { field: 'parentBookableProducts' },
              { field: 'dependantProducts' }
            ]
          })
        })
      )
    } else {
      this.selectedProductIds = []
      if (this.changeOfResourceProduct) {
        this.form.controls.productIds.patchValue(this.selectedProductIds)
      } else {
        if (!this.createBookingFromSchedule) {
          this.form.controls.productIds.setValue([])
        }
      }
    }
  }
  public applyYearMatrix(yearMatrix) {
    this.wantApplyYearMatrix = yearMatrix
    this.onApplyYearCalendar.emit(yearMatrix)
  }
  public changeProductIds(event) {
    this.changeOfResourceProduct = true
  }
  public mappedProducts(products) {
    if (
      this.form.controls['linkedProductId'].value &&
      [BookingCreationType.DEFAULT].includes(this.selectedBookingCreateType)
    ) {
      let bookableProducts = []
      let productsEntities
      this.store
        .select(productSelectors.selectEntities)
        .pipe(take(1))
        .subscribe((pr) => (productsEntities = pr))
      const selectedProductParentBookable =
        productsEntities[this.form.controls['linkedProductId'].value]
          .parentBookableProducts ?? []
      selectedProductParentBookable.map((bk) =>
        bookableProducts.push(productsEntities[bk])
      )
      return bookableProducts
    }
    return products
  }
  private getBookableProducts(pId, productEntities) {
    return productEntities[pId].parentBookableProducts
      ? productEntities[pId]?.parentBookableProducts?.map(
          (id) => productEntities[id]
        )
      : []
  }
  public onSelectAllowedLinkedProductIds(allowedLinkedProductIds) {
    if (allowedLinkedProductIds?.length > 0) {
      this.store.dispatch(
        productActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              id: { [CondOperator.IN]: allowedLinkedProductIds.map((pr) => pr) }
            },
            join: [
              { field: 'parentBookableProducts' },
              { field: 'dependantProducts' }
            ]
          })
        })
      )
      this.form.controls.allowedLinkedProductIds.setValidators([
        Validators.required,
        Validators.minLength(2)
      ])
    } else {
      this.selectedProductIds = []
    }

    this.form.controls.allowedLinkedProductIds.updateValueAndValidity()
  }
  public setDates(date) {
    if (moment(date).isValid() && !moment(this.form.value.endDate).isValid()) {
      this.lastStartDate = date
      this.form.controls.endDate.patchValue(moment(date).add(1, 'hour'))
    } else if (
      moment(date).isValid() &&
      moment(this.form.value.endDate).isValid()
    ) {
      this.lastStartDate = date
      const endDate = moment(this.form.value.endDate)
      endDate.date(moment(date).date())
      endDate.month(moment(date).month())
      endDate.year(moment(date).year())
      this.form.controls.endDate.patchValue(endDate)
    }
    let f = this.form.value
    f['startDate'] = moment(this.lastStartDate)
    this.onValueChanges.emit({
      ...f,

      colour:
        f.colour == DEFAULT_COLOR || !f?.colour
          ? null
          : hexColorToDecimal(f.colour)
    })
  }
  public setEndDate(type: EndTimeSlot): void {
    const {
      startDate,
      endDate,
      linkedProductId,
      productId,
      colour,
      productIds
    } = this.form.value
    if (!startDate) return

    const patchEndDate = (minutesToAdd: number) => {
      const newEndDate = moment(startDate).add(minutesToAdd, 'minutes')
      if (!endDate || newEndDate.valueOf() !== moment(endDate).valueOf()) {
        this.form.controls.endDate.patchValue(newEndDate)
      }
    }

    switch (type) {
      case EndTimeSlot.CUSTOM:
        this.selectedEndTimeSlot = EndTimeSlot.CUSTOM
        return
      case EndTimeSlot.ONE_HOUR:
        this.selectedEndTimeSlot = EndTimeSlot.ONE_HOUR
        return patchEndDate(60)
      case EndTimeSlot.ONE_AND_HALF_HOURS:
        this.selectedEndTimeSlot = EndTimeSlot.ONE_AND_HALF_HOURS
        return patchEndDate(90)
      case EndTimeSlot.TWO_HOURS:
        this.selectedEndTimeSlot = EndTimeSlot.TWO_HOURS
        return patchEndDate(120)
      case EndTimeSlot.PRODUCT_SLOT_SIZE: {
        this.selectedEndTimeSlot = EndTimeSlot.PRODUCT_SLOT_SIZE
        this.store
          .select(productSelectors.selectEntities)
          .pipe(take(1))
          .subscribe((productEntities) => {
            const targetProductId = linkedProductId ?? productId
            const slotMinutes = productEntities[targetProductId]?.slotMinutes

            if (targetProductId && slotMinutes) {
              patchEndDate(slotMinutes)
            }
            const parentProducts =
              productEntities[linkedProductId]?.parentBookableProducts ?? []

            if (
              linkedProductId &&
              !productIds?.length &&
              parentProducts.length
            ) {
              this.form.controls.productIds.patchValue(parentProducts)
            }

            this.onValueChanges.emit({
              ...this.form.value,
              colour:
                colour === DEFAULT_COLOR ? null : hexColorToDecimal(colour)
            })
          })
        return
      }
    }
  }

  public dateValidator(
    control: AbstractControl
  ): { [key: string]: boolean } | null {
    if (!moment(control?.value).isValid()) {
      return { dateNotValid: true }
    }

    return null
  }
  public patchStartDate(date) {
    this.form.controls.startDate.patchValue(moment(date))
  }
  public patchEndDate(date) {
    let f = this.form.value
    f['endDate'] = moment(date)
    this.onValueChanges.emit({
      ...f,
      colour:
        f.colour == DEFAULT_COLOR || !f?.colour
          ? null
          : hexColorToDecimal(f.colour)
    })
  }
  public onFormSubmit(): void {
    const f = this.form.value
    if (
      f.maxParticipants < this.booking?.currentParticipantCount &&
      f.maxParticipants != null
    ) {
      this.form.controls.maxParticipants.markAsDirty()
      this.form.controls.maxParticipants.setErrors({ invalid: true })

      this.notificationService.emit(
        'general.maxParticipantsLessThanCurrent',
        'warning'
      )
      return
    }
    if (moment(f.endDate).valueOf() <= moment(f.startDate).valueOf()) {
      this.notificationService.emit('general.incorrectEndDate', 'warning')
      return
    }
    if (!moment(f.endDate).isValid()) {
      this.form.controls['endDate'].setErrors({ dateNotValid: true })
    }
    if (!moment(f.startDate).isValid()) {
      this.form.controls['startDate'].setErrors({
        dateNotValid: true,
        invalid: true
      })
    }
    if (
      this.form.controls['courseId'].value &&
      this.form.controls['productIds'].value.length > 1
    ) {
      this.form.controls['productIds'].setErrors({
        numberOfProductsNotValid: true
      })
      this.translate
        .get('general.errorResources')
        .pipe(take(1))
        .subscribe((msg) => {
          this.notificationService.emit(msg, 'warning')
        })
    }
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })
      return
    }

    this.onSubmit.emit({
      ...f,
      colour:
        f.colour == DEFAULT_COLOR || !f?.colour
          ? null
          : hexColorToDecimal(f.colour),
      status: f.status !== null ? parseInt(f.status) : null
    })
  }

  public attemptSubmit(): void {
    Object.keys(this.form.controls).map((controlName) =>
      this.form.controls[controlName].markAsDirty()
    )

    this.formRef.ngSubmit.emit(undefined)
  }
  public getParticipants$() {
    return combineLatest([
      this.store.select(productSelectors.selectEntities)
    ]).pipe(
      take(1),
      map(([productEntities]) => {
        const { linkedProductId, productIds, allowedLinkedProductIds } =
          this.form?.value
        const arrayProductIds = Array.from(
          new Set(
            [
              ...(Array.isArray(allowedLinkedProductIds)
                ? allowedLinkedProductIds
                : []),
              ...(Array.isArray(productIds) ? productIds : []),
              linkedProductId
            ].filter((id) => id !== null && id !== undefined)
          )
        )

        const maxMaxParticipants = arrayProductIds.reduce((min, prodId) => {
          const maxParticipants =
            productEntities[prodId]?.maxParticipants ?? null
          return Math.max(min, maxParticipants)
        }, null)
        return !arrayProductIds || arrayProductIds?.length == 0
          ? null
          : maxMaxParticipants
      })
    )
  }

  ngOnDestroy() {
    this.subscriptions.filter((s) => Boolean(s)).map((s) => s.unsubscribe())
  }

  public onSearchAllowedLinkedProduct(value: any) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            isActive: 1,
            description: { [CondOperator.CONTAINS_LOW]: value.term }
          },
          join: [
            { field: 'parentBookableProducts' },
            { field: 'dependantProducts' }
          ]
        })
      })
    )
  }
  public toggleColorByDefault() {
    this.form.controls.colour.patchValue(this.default_booking_color)
  }
  public onSearchProducts(term: string) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            isActive: 1,
            description: { [CondOperator.CONTAINS_LOW]: term }
          },
          join: [
            { field: 'parentBookableProducts' },
            { field: 'dependantProducts' }
          ]
        })
      })
    )
  }
  public openModalRepeatbookings(openRepeatBooking: boolean) {
    this.onOpenModalRepeatbookings.emit(openRepeatBooking)
  }
  public clickOnCancelButton() {
    this.onClickCancelButtonRepeat.emit()
  }
  public clickOnSaveButton({
    disabledDates,
    wantApplyYearMatrix
  }: {
    disabledDates: Date[]
    wantApplyYearMatrix: boolean
  }) {
    this.onClickOnSaveButtonRepeat.emit({ disabledDates, wantApplyYearMatrix })
  }
  public setSelectedFormInBooKingFormRepeat() {
    this.onSetSelectedFormInBooKingFormRepeat.emit()
  }
  public openModalAssignSupervisor(openModal: boolean) {
    this.onClickOpenModalAssignSupervisor.emit(openModal)
  }
  public showHideSection() {
    this.showHideFields = !this.showHideFields
  }
}
